import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createFloor, updateFloor } from "../../services/WarehouseSettings";
import { IFloorRequestDTO } from "../../interfaces/WarehouseSettings";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditFloorProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  buildingId: string;
  onSuccess: (message: string) => void;
}

const AddEditFloor: React.FC<AddEditFloorProps> = ({
  open,
  onClose,
  editData,
  buildingId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    level: "",
    floorName: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;
  const COMPANY_ID = loggedInUser?.companyId;

  useEffect(() => {
    if (editData) {
      const levelValue = editData.level ?? editData.floorNo ?? editData.floorNumber ?? editData.levelNumber ?? "";
      setFormData({
        level: String(levelValue),
        floorName: editData.floorName || editData.name || "",
      });
    } else {
      setFormData({
        level: "",
        floorName: "",
      });
    }
    setErrors({});
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.level) newErrors.level = "Level number is required";
    if (!formData.floorName) newErrors.floorName = "Floor name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID || !WAREHOUSE_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IFloorRequestDTO = {
        companyId: COMPANY_ID,
        warehouseId: WAREHOUSE_ID,
        buildingId: Number(buildingId),
        floorName: formData.floorName,
        level: Number(formData.level),
      };

      const response = isEdit 
        ? await updateFloor(editData.id, dto)
        : await createFloor(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Floor updated successfully" : "Floor added successfully";
        onSuccess(message);
        onClose();
      } else {

        const errorMsg = response?.errorResponse?.response?.data?.message || 
                         response?.errorResponse?.message || 
                         `Error ${isEdit ? 'updating' : 'creating'} floor`;
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err.message || err);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>
            {editData ? "Edit Floor" : "Add Floor"}
          </Typography>
          <Typography sx={dialogDescriptionStyles}>
            {editData ? "Edit Floor" : "Add Floor"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Floor Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Ground Floor"
              value={formData.floorName}
              onChange={(e) => {
                setFormData({ ...formData, floorName: e.target.value });
                setErrors({ ...errors, floorName: "" });
              }}
              error={!!errors.floorName}
              helperText={errors.floorName}
              sx={formFieldStyles}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Level Number <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="eg. 0 for ground, -1 for basement"
              value={formData.level}
              onChange={(e) => {
                setFormData({ ...formData, level: e.target.value });
                setErrors({ ...errors, level: "" });
              }}
              error={!!errors.level}
              helperText={errors.level || "Use 0 for ground, negative for basement"}
              sx={formFieldStyles}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 40px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : (editData ? "Save" : "Add")}
        </Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditFloor;